import React, { FunctionComponent, Fragment, memo, useState } from 'react';
import { convertNumbersToProperFormat } from 'utils/number';
import { LimitationTitleCell, UserRoleBadges } from './styles';
import messages from './Messages';
import Chevron from 'components/Chevron';
import Badge from 'components/Badge';
import CollapsibleBody from 'components/Collapsible/CollapsibleBody';
import { getHighlightedText } from '../../../../utils/textSearch';
import Icon from '../../../Icon/Icon';
import { ChangeLimitationType } from '../LimitationManager';
import LimitationButtonGroup from '../LimitationButtonGroup';
import { LimitationButtonGroupItem } from '../LimitationButtonGroup/ButtonGroup';
import {
  ILimitationState,
  LimitationType,
} from 'store/limitations/limitationsSlice';
import { ICompany, ICompanyRole } from 'store/companies/companiesTypes';
import { appDispatch, IUsers } from 'store/store';
import {
  ActivePermissions,
  IProviderItemView,
} from 'store/providers/providerTypes';
import Input from '../../../Input';
import {
  IMultipleChoiceQuestionView,
  ISurveyItem,
  ISurveyView,
} from '../../../../store/survey/surveyTypes';
import { updateQuestion } from '../../../../store/survey/surveySlice';
import { AutoRolePermissionSchemeIndex } from '../../../../store/companies/companiesUtils';
import i18n from '../../../../i18n';
import notify from 'components/Notify';

type ComponentProps = {
  item: IProviderItemView | null;
  isActive: boolean;
  isDisabled: boolean;
  isOwner: boolean;
  onChangeActiveRole: (
    e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>
  ) => void;
  onChangeLimitation: (
    limitationType: LimitationType,
    checked: boolean,
    itemType: ChangeLimitationType,
    limitationId: number,
    itemId: number
  ) => void;
  role: ICompanyRole;
  users: IUsers['map'];
  readLimitation: ILimitationState;
  reportLimitation: ILimitationState;
  changeDataLimitation: ILimitationState;
  editLimitation: ILimitationState;
  loadingForRead: boolean;
  loadingForReport: boolean;
  loadingForChangeData: boolean;
  loadingForEdit: boolean;
  parentActivePermission?: ActivePermissions;
  filterString?: string;
  showEditLimitation: boolean;
  showReportLimitation: boolean;
  showChangeDataLimitation: boolean;
  showReadLimitation: boolean;
  fillTextOverwrite?: string;
  fillPermissionSchemeActive: boolean;
  reportPermissionSchemeActive: boolean;
  survey: ISurveyView | undefined | null;
  permissionScheme: ICompany['permissionSchemes'];
};

const LimitationRoleRow: FunctionComponent<ComponentProps> = ({
  isActive,
  isDisabled,
  onChangeActiveRole,
  onChangeLimitation,
  role,
  users,
  readLimitation,
  reportLimitation,
  changeDataLimitation,
  editLimitation,
  loadingForRead,
  loadingForReport,
  loadingForChangeData,
  loadingForEdit,
  parentActivePermission,
  filterString,
  showEditLimitation,
  showReportLimitation,
  showChangeDataLimitation,
  showReadLimitation,
  reportPermissionSchemeActive,
  fillPermissionSchemeActive,
  fillTextOverwrite,
  item,
  survey,
  permissionScheme,
}) => {
  const itemId = role.id;
  const editPermissionFromParent =
    parentActivePermission?.effectiveEditPermission?.includes('R' + itemId);
  const changeDataPermissionFromParent =
    parentActivePermission?.effectiveChangeDataPermission?.includes(
      'R' + itemId
    );
  const reportPermissionFromParent =
    parentActivePermission?.effectiveReportPermission?.includes('R' + itemId);
  const readPermissionFromParent =
    parentActivePermission?.effectiveReadPermission?.includes('R' + itemId);
  const canEditEdit = !editPermissionFromParent;
  const canEditChangeData = canEditEdit && !changeDataPermissionFromParent;
  const canEditReport = canEditChangeData && !reportPermissionFromParent;
  const canEditRead = canEditReport && !readPermissionFromParent;
  const readChecked = readLimitation?.exceptedRoles?.some(
    (id) => id === itemId
  );
  const reportChecked = reportLimitation?.exceptedRoles?.some(
    (id) => id === itemId
  );
  const changeDataChecked = changeDataLimitation?.exceptedRoles?.some(
    (id) => id === itemId
  );
  const editChecked = editLimitation?.exceptedRoles?.some(
    (id) => id === itemId
  );

  const availableRole = Object.values(permissionScheme || {}).find(
    (item) => item.id === role?.id + 1000000000
  );

  const question = (Object.values(survey?.questionMap || {})?.find(
    (item) =>
      item?.data?.type === 'multipleChoice' &&
      item?.data?.useTags &&
      !item?.data?.deleted
  )?.data || {}) as IMultipleChoiceQuestionView;

  const selectedValue = editChecked
    ? 'edit'
    : changeDataChecked
      ? 'changeData'
      : reportChecked
        ? 'report'
        : readChecked
          ? 'read'
          : 'none';
  const handleChangeSelect = (selected?: LimitationButtonGroupItem) => {
    if (selected?.disabled || selected?.fromParent) {
      notify(
        i18n.t('permissionIsFromParent', { ns: 'documentManager' }),
        'warning'
      );
      return;
    }
    const value = selected?.type || 'none';
    function process(
      readChecked: boolean,
      limitationType: LimitationType,
      limitationId: number
    ) {
      const itemType = 'role' as ChangeLimitationType;
      if (readChecked && value !== limitationType)
        onChangeLimitation(
          limitationType,
          false,
          itemType,
          limitationId,
          itemId
        );
      if (!readChecked && value === limitationType)
        onChangeLimitation(
          limitationType,
          true,
          itemType,
          limitationId,
          itemId
        );
    }
    process(readChecked, 'read', readLimitation?.id);
    process(reportChecked, 'report', reportLimitation?.id);
    process(changeDataChecked, 'changeData', changeDataLimitation?.id);
    process(editChecked, 'edit', editLimitation?.id);
  };
  const permissionOptions = [] as LimitationButtonGroupItem[];
  if (showReadLimitation)
    permissionOptions.push({
      fromParent: !!readPermissionFromParent,
      selected: selectedValue === 'read',
      type: 'read',
      disabled: isDisabled || !canEditRead,
      onlyInPart: fillPermissionSchemeActive,
      titleOverwrite: fillTextOverwrite,
    });
  if (showReportLimitation)
    permissionOptions.push({
      fromParent: !!reportPermissionFromParent,
      selected: selectedValue === 'report',
      type: 'report',
      disabled: isDisabled || !canEditReport,
      onlyInPart: reportPermissionSchemeActive,
    });
  if (showChangeDataLimitation)
    permissionOptions.push({
      fromParent: !!changeDataPermissionFromParent,
      selected: selectedValue === 'changeData',
      type: 'changeData',
      disabled: isDisabled || !canEditChangeData,
    });
  if (showEditLimitation)
    permissionOptions.push({
      fromParent: !!editPermissionFromParent,
      selected: selectedValue === 'edit',
      type: 'edit',
      disabled: isDisabled || !canEditEdit,
    });
  const [fillAccessToQuestion, setFillAccessToQuestion] =
    React.useState<boolean>(() => {
      return (
        question?.fillPermissionSchemeList?.includes(role?.id + 1000000000) ||
        false
      );
    });
  const [reportAccessToQuestion, setReportAccessToQuestion] =
    React.useState<boolean>(() => {
      return (
        question?.reportPermissionSchemeList?.includes(role?.id + 1000000000) ||
        false
      );
    });
  const [loadingFillAccess, setLoadingFillAccess] = useState(false);
  const [loadingReportAccess, setLoadingReportAccess] = useState(false);

  const handleChangePermission = (type: string, addOrDelete: boolean) => {
    // add or delete if it was true : add, or else delete
    if (survey) {
      const reportPermissionScheme = new Set(
        question.reportPermissionSchemeList
      );
      const fillPermissionScheme = new Set(question.fillPermissionSchemeList);
      if (type === 'fill' && addOrDelete) {
        fillPermissionScheme.add(role?.id + AutoRolePermissionSchemeIndex);
        setLoadingFillAccess(true);
      } else if (
        type === 'fill' &&
        fillPermissionScheme.has(role?.id + AutoRolePermissionSchemeIndex)
      ) {
        fillPermissionScheme.delete(role?.id + AutoRolePermissionSchemeIndex);
        setLoadingFillAccess(true);
      }

      if (type === 'report' && addOrDelete) {
        reportPermissionScheme.add(role?.id + AutoRolePermissionSchemeIndex);
        setLoadingReportAccess(true);
      } else if (
        type === 'report' &&
        reportPermissionScheme.has(role?.id + AutoRolePermissionSchemeIndex)
      ) {
        reportPermissionScheme.delete(role?.id + AutoRolePermissionSchemeIndex);
        setLoadingReportAccess(true);
      }
      const updatedFillPermissionScheme = Array.from(fillPermissionScheme);
      const updatedReportPermissionScheme = Array.from(reportPermissionScheme);

      if (question) {
        appDispatch(
          updateQuestion({
            questionData: {
              ...(question as IMultipleChoiceQuestionView),
              fillPermissionSchemeList: updatedFillPermissionScheme,
              reportPermissionSchemeList: updatedReportPermissionScheme,
            },
            providerSurveyData: item as ISurveyItem,
          })
        ).then((_: any) => {
          setLoadingReportAccess(false);
          setLoadingFillAccess(false);
        });
      }
    }
  };

  const usersList = role.users
    .reduce((acc: number[], item) => {
      if (!acc.includes(item.userId)) acc.push(item.userId);
      return acc;
    }, [])
    .reduce((acc: string[], userId) => {
      const user = Object.values(users).find((u) => u!.id === userId);
      if (user) acc.push(user.name);
      return acc;
    }, []);

  return (
    <Fragment>
      <div
        id="--role_name--"
        data-role-id={itemId}
        onClick={onChangeActiveRole}
        style={{ padding: '15px 5px 0px' }}
      >
        <LimitationTitleCell>
          <Chevron isOpen={isActive} />
          <Icon type="briefcase" style={{ marginInlineEnd: '10px' }} />
          <span>
            {getHighlightedText(
              messages.roleTitleWithUserCount(
                role.displayName!,
                convertNumbersToProperFormat(usersList.length)
              ),
              filterString
            )}
          </span>
        </LimitationTitleCell>
        <div style={{ padding: '0px 5px 15px' }}>
          <CollapsibleBody
            isOpen={isActive}
            customBodyStyle={{ padding: '0px 25px' }}
          >
            <UserRoleBadges>
              {usersList.map((u, idx) => {
                return (
                  <Badge
                    key={`${itemId}_${idx}}`}
                    hollow={true}
                    radius={4}
                    color="darkGray"
                  >
                    {getHighlightedText(u, filterString)}
                  </Badge>
                );
              })}
            </UserRoleBadges>
          </CollapsibleBody>
        </div>
      </div>
      <div style={{ padding: '15px 5px 0px' }}>
        <LimitationButtonGroup
          style={{}}
          width="undefined"
          onSelect={handleChangeSelect}
          items={permissionOptions}
          loading={
            loadingForRead ||
            loadingForReport ||
            loadingForChangeData ||
            loadingForEdit
          }
        />
        <div className="flex gap-3 items-center my-2">
          {availableRole &&
            Object.keys(question).length > 0 &&
            permissionOptions.map((permission, index) => {
              const isFillAccess = permission.selected || permission.fromParent;
              const showFillAccess =
                (permission.type === 'read' || permission.type === 'report') &&
                isFillAccess;
              const showReportAccess =
                permission.type === 'report' && isFillAccess;

              if (!showFillAccess && !showReportAccess) {
                return null;
              }

              return (
                <>
                  {showFillAccess && (
                    <Input
                      key={`fill-${index}-${role.id}`}
                      id={`fill-${index}-${role.id}`}
                      type="checkbox"
                      label={
                        i18n.t('fillAccess', {
                          ns: 'documentManager',
                        }) as string
                      }
                      checked={fillAccessToQuestion}
                      onChange={(event) => {
                        setFillAccessToQuestion(event.target.checked);
                        handleChangePermission('fill', event.target.checked);
                      }}
                      loading={loadingFillAccess}
                    />
                  )}

                  {showReportAccess && (
                    <Input
                      key={`report-${index}-${role.id}`}
                      id={`report-${index}-${role.id}`}
                      type="checkbox"
                      label={
                        i18n.t('reportAccess', {
                          ns: 'documentManager',
                        }) as string
                      }
                      checked={reportAccessToQuestion}
                      onChange={(event) => {
                        setReportAccessToQuestion(event.target.checked);
                        handleChangePermission('report', event.target.checked);
                      }}
                      loading={loadingReportAccess}
                    />
                  )}
                </>
              );
            })}
        </div>
      </div>
    </Fragment>
  );
};

export default memo(LimitationRoleRow);
